import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmSubjectType } from 'APIPackage/CatalogsModule/interfaces/IKnmSubjectType';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getCatalogKnmSubjectTypes } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmSubjectTypeForm = Omit<IKnmSubjectType, 'id'>;

interface IKnmSubjectTypeEditFormProps {
  onBack: () => void;
}

interface IKnmSubjectTypeEditFormStoreProps {
  knmSubjectTypes: IKnmSubjectType[];
}

const KnmSubjectTypeEditFormWithoutStore = ({
  onBack,
  knmSubjectTypes,
}: IKnmSubjectTypeEditFormProps & IKnmSubjectTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmSubjectType, setSelectedKnmSubjectType] = useState<IKnmSubjectType | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES_ALL));
  }, [dispatch]);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmSubjectType = knmSubjectTypes.find(k => k.id.toString() === id);

      setSelectedKnmSubjectType(prev => (prev?.id === knmSubjectType?.id ? undefined : knmSubjectType));
    },
    [knmSubjectTypes],
  );

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmSubjectType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
      weight: 0,
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmSubjectType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmSubjectTypeForm) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES_ALL }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmSubjectType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES_ALL,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmSubjectType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmSubjectTypeForm>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Вес" color="black">
            <TextInputField min={1} max={999999999} type="number" name="weight" />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmSubjectTypeForm> onSubmit={onSubmit} initialValues={selectedKnmSubjectType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Типы субъектов КНМ</span>
        {knmSubjectTypes &&
          knmSubjectTypes.map((knmSubjectType: IKnmSubjectType) => (
            <button
              key={knmSubjectType.id}
              className={selectedKnmSubjectType?.id === knmSubjectType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmSubjectType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmSubjectType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmSubjectType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmSubjectTypeEditFormStoreProps => ({
  knmSubjectTypes: getCatalogKnmSubjectTypes(state),
});

export const KnmSubjectTypeEditForm = connect(mapStateToProps)(KnmSubjectTypeEditFormWithoutStore);
